// shared/src/oracle/observation.ts
// The Observation contract — the ONE shape every data source emits, so a single
// framework serves price + macro (both live) and news (not yet built). See
// docs/data-oracle-2026-07-13-design.md §3.
//
// The load-bearing detail is observed_at vs fetched_at: a source can be UP
// (fetched_at current) but return STALE data (observed_at old) — the exact ETH
// bug (§1.2a, a Yahoo spark frozen for an hour). Making every source declare
// when its DATA is timestamped lets the resolver weigh/reject on freshness, so
// "a successful call" is never confused with "fresh data".

import { z } from 'zod';

/** Data types the oracle resolves. Mode is per (data_type, asset_class) in
 *  oracle_policy — NOT a property of the data_type: crypto/thin price = CONSENSUS,
 *  equity price = AUTHORITATIVE (migration 0100), macro = AUTHORITATIVE.
 *  News (UNION) is the next instance. */
export type OracleDataType = 'price' | 'macro';

/** Qualitative per-observation flags. `error:<code>` marks a failed/absent fetch;
 *  such an observation carries value=null and is excluded from the resolve. NOT a
 *  numeric anchor to any LLM (feedback_no_numeric_anchors_to_llm) — categorical only. */
export type QualityFlag =
  | 'ok'
  | 'stale'
  | 'partial'
  | 'depeg'      // a stablecoin leg (USDC/USDT) deviated from $1 → down-weight this source's USD conversion
  | 'derived'    // computed via a cross-rate (e.g. WBTC/ETH pool × ETH-USD)
  | 'spot'       // a single-block SPOT read, NOT a manipulation-resistant TWAP mean. Carried by
                 // v4 pools (no observe()) and by v3 pools whose observationCardinality is 1
                 // (no usable oracle). Makes the weaker read VISIBLE rather than passing a spot
                 // off as a TWAP — the resolver/dash can see what it is actually consensusing.
  | `error:${string}`;

/** One source's reading of one key at one moment. */
export interface Observation {
  readonly source_id:   string;         // 'cryptocom' | 'yahoo' | 'univ3_eth_usdc' | ...
  readonly data_type:   OracleDataType;
  readonly key:         string;         // '$ETH' | '$BTC'
  readonly value:       number | null;  // null when errored/absent
  readonly observed_at: string | null;  // ISO-Z of the DATA at source (null on error)
  readonly fetched_at:  string;         // ISO-Z when we called
  readonly quality:     ReadonlyArray<QualityFlag>;
}

export const ObservationSchema = z.object({
  source_id:   z.string().min(1),
  data_type:   z.enum(['price', 'macro']),
  key:         z.string().min(1),
  value:       z.number().finite().nullable(),
  observed_at: z.string().nullable(),
  fetched_at:  z.string(),
  quality:     z.array(z.string()),
});

/** Convenience: an error observation (value/observed_at null, quality error:<code>).
 *  `dataType` defaults to 'price' (the original price-family callers); the macro
 *  producer passes 'macro'. */
export function errorObservation(
  source_id: string,
  key:       string,
  code:      string,
  fetchedAtIso: string,
  dataType:  OracleDataType = 'price',
): Observation {
  return {
    source_id,
    data_type:   dataType,
    key,
    value:       null,
    observed_at: null,
    fetched_at:  fetchedAtIso,
    quality:     [`error:${code}` as QualityFlag],
  };
}
